import { EventMembershipTypes } from "@api/enums/EventMembershipTypes";
import { AgendaModel } from "@api/models/agenda/AgendaModel";
import { EventMembership, MembershipsCreateParams } from "@api/models/EventMemberShip";
import { Element } from "@api/models/Element"
import { agendaConverter } from "@api/models/objects/Converters";
import { SessionEditModel } from "@api/models/session/SessionEditModel";
import { EventMembershipRepository } from "@api/repositories/EventMembershipRepository";
import { ObjectRepository } from "@api/repositories/ObjectRepository";
import { SettingRepository } from "@api/repositories/SettingRepository";
import { Store } from "vuex";
import { ArrayHelpers } from "@common/ArrayHelpers";
import { EventMembershipStatuses } from "@api/enums/EventMembershipStatuses";
import { EventMembershipsService, EventMembershipsServiceInterface } from "./EventMembershipsService";
import { ObjectDataParamsInterface } from "@api/repositories/interfaces/ObjectDataParamsInterface";
import { QueryParametersInterface } from "@api/repositories/interfaces/QueryParametersInterface";
import { isAxiosError } from "@api/helpers/errorHelpers";
import { saveHandler, errorHandler } from "../helpers/httpHandlers";
import {settingsActionsLists} from "@service/store/settings/actions-list";

interface SessionAdminServiceInterface {
    save(sessionId: string | undefined, sessionModel: SessionEditModel): void;
    init(sessionId: string | null): void;
    getSessionByLangs(id: string, languages: string[]): any | void;
    getSpeakers(sessionId: string): Promise<EventMembership[] | undefined>;
    getSessionRefs(query: QueryParametersInterface): Promise<Element[] | undefined>;
}

export class SessionAdminService implements SessionAdminServiceInterface {

    constructor(
        private agendaSchemaId: string,
        private objectRepository: ObjectRepository,
        private settingsRepository: SettingRepository,
        private eventMembershipRepository: EventMembershipRepository,
        private store: Store<any>
    ) {
    }

    languages: string[] | null = null;

    speakersMemberships: Element[];
    originSpeakersIds: number[] = [];

    eventMembershipsService: EventMembershipsServiceInterface | null = null;

    /**
     * initialization
     */
    async init() {
        await this.getLanguages();
    }

    async getLanguages() {
        this.languages = await this.store.dispatch(settingsActionsLists.loadLanguages);
    }

    /**
     * getting session by each languages - need for filling SessionEditModel
     * @param id
     * @param languages
     * @returns
     */
    async getSessionByLangs(id: string, languages: string[]) {
        try {
            return await this.objectRepository.getByLanguages<AgendaModel>({
                schema: this.agendaSchemaId,
                objectId: id,
                converter: agendaConverter
            }, languages);
        }
        catch(e) {
            const error = e;

            if (isAxiosError<any>(error) && error.response?.status === 404) {
                return null;
            }
            else {
                this.store.dispatch('reportException', e, { root: true });
            }
        }
    }

    /**
     * getting eventMemberships data for speakers control
     * @param sessionId
     * @returns
     */
    async getSpeakers(sessionId: string) {
        try {
            const speakers = (await this.eventMembershipRepository.speakers(this.agendaSchemaId, [sessionId]))[sessionId];
            this.speakersMemberships = (await this.eventMembershipRepository.query(this.agendaSchemaId, [sessionId], [EventMembershipTypes.speaker]));
            this.originSpeakersIds = this.speakersMemberships.map((item) => item.userId);
            return speakers;
        }
        catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    async getSessionRefs(query: QueryParametersInterface) {
        try {
            return (await this.objectRepository.query(this.agendaSchemaId, query));
        }
        catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    /**
     * Save form data (create or update)
     * @param sessionId
     * @param sessionModel
     * @param lang
     */
    async save(sessionId: string | undefined, sessionModel: SessionEditModel, lang = 'en') {

        let id: string | undefined = undefined;

        try {
            //CREATE:
            if (!sessionId) {
                id = await this.create(sessionModel, lang);

                if (id && sessionModel.speakerUserIds?.length) {
                    this.addSpeakers(id, sessionModel.speakerUserIds)
                }
            }
            //UPDATE:
            else {
                const params = this.getSessionDataParams(sessionModel, lang);

                const updated = await this.update(sessionId, params);

                if (updated) {
                    this.updateSpeakers(sessionId, sessionModel.speakerUserIds || []);

                    id = updated.fields?.id as string;
                }
            }

            saveHandler(this.store.dispatch, id, sessionId);

            return id;
        }
        catch(e) {
            const error = e;

            if (isAxiosError<any>(error) && error.response?.status === 409) {
                errorHandler(this.store.dispatch, error.response.data);
            }
            else {
                this.store.dispatch('reportException', e, { root: true });
                saveHandler(this.store.dispatch);
            }
        }
    }

    /**
     * create session
     * @param sessionModel
     * @param lang
     * @returns
     */
    private async create(sessionModel: SessionEditModel, lang: string) {
        const data = sessionModel.getFields(lang);
        const params = this.getSessionDataParams(sessionModel, lang);

        const { id } = await this.objectRepository.create<AgendaModel>(this.agendaSchemaId, data, agendaConverter);

        // if languages exists
        if (id && params.languages) {
            await this.update(id, params);
        }

        return id;
    }

    /**
     * Update session
     * @param sessionId
     * @param sessionData
     * @returns
     */
    private async update(sessionId: string, sessionData: ObjectDataParamsInterface): Promise<ObjectDataParamsInterface | undefined> {
        return await this.objectRepository.updateV2(this.agendaSchemaId, sessionId, sessionData);
    }

    private getSessionDataParams(sessionModel: SessionEditModel, lang: string) {
        let languages = sessionModel.getLanguages(this.languages, lang);
        let fields = sessionModel.getFields(lang);

        let sessionData: ObjectDataParamsInterface = {
            languages,
            fields
        }

        return sessionData;
    }

    private updateSpeakers(sessionId: string, newSpeakerUserIds: number[]) {

        const eventMembershipsService = this.getEventMembershipsService();

        const originSpeakerUserIds = this.originSpeakersIds;
        //получение разницы для последующих действий
        const userIdsForCreate = ArrayHelpers.difference(newSpeakerUserIds, originSpeakerUserIds);
        const userIdsForDelete = ArrayHelpers.difference(originSpeakerUserIds, newSpeakerUserIds);

        if (userIdsForCreate.length) {
            this.addSpeakers(sessionId, userIdsForCreate, eventMembershipsService);
        }

        if (userIdsForDelete.length) {
            this.deleteSpeakers(userIdsForDelete);
        }

        //сбрасываем исходные userIds спикеров
        this.originSpeakersIds = newSpeakerUserIds;
    }

    private addSpeakers(sessionId: string, userIdsForCreate: number[], eventMembershipsService = this.getEventMembershipsService()) {

        const membershipsForCreate: MembershipsCreateParams = {
            model: {
                eventSchemaId: this.agendaSchemaId,
                eventObjectId: sessionId,
                status: EventMembershipStatuses.confirmed,
                type: EventMembershipTypes.speaker
            },
            userIds: userIdsForCreate
        }

        eventMembershipsService.create(membershipsForCreate);
    }

    private deleteSpeakers(userIdsForDelete: number[], eventMembershipsService = this.getEventMembershipsService()) {
        const membershipIdsForDelete = this.speakersMemberships.filter((membership) => userIdsForDelete.includes(membership.userId)).map(x => x.id);
        eventMembershipsService.delete(membershipIdsForDelete);
    }

    private getEventMembershipsService() {
        if (!this.eventMembershipsService) {
            return this.eventMembershipsService = new EventMembershipsService(this.eventMembershipRepository, this.store);
        }
        else {
            return this.eventMembershipsService;
        }
    }
}
